import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Table, Button, Badge, Spinner, Alert, Pagination, Form, InputGroup 
} from 'react-bootstrap';
import { FiPlus, FiSearch, FiFilter, FiFileText, FiCheck, FiEye } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const ReportList = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState({});
  const [resolvingId, setResolvingId] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    type: '',
    status: '',
    search: '',
    reporter: ''
  });

  const navigate = useNavigate();

  const fetchReports = async () => {
    try {
      setLoading(true);
      const endpoint = user.role === 'supervisor' ? '/reports' : '/reports/my-reports';
      const params = new URLSearchParams(filters).toString();
      const response = await api.get(`${endpoint}?${params}`);
      
      setReports(response.data.data);
      setMeta(response.data.meta);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [filters]);

  const handleResolve = async (reportId) => {
    try {
      setResolvingId(reportId);
      await api.patch(`/reports/${reportId}/resolve`);
      setReports(reports.map(report => 
        report.id === reportId ? { ...report, status: 'resolved' } : report
      ));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resolve report');
    } finally {
      setResolvingId(null);
    }
  };

  const handleFilterChange = (e) => {
    setFilters(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
      page: 1
    }));
  };
  

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };
  console.log('reports:',reports);

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <FiFileText className="me-2" />
          {user.role === 'supervisor' ? 'All Reports' : 'My Reports'}
        </h2>
      </div>

      {/* Filters */}
      <div className="report-filters mb-4 bg-light p-3 rounded">
        <div className="row g-3">
          {user.role === 'supervisor' && (
            <div className="col-md-3">
              <Form.Control
                type="text"
                name="reporter"
                placeholder="Filter by reporter"
                value={filters.reporter}
                onChange={handleFilterChange}
              />
            </div>
          )}
          
          <div className="col-md-3">
            <Form.Select 
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
            >
              <option value="">All Types</option>
              <option value="SAFETY">Safety</option>
              <option value="DAMAGE">Damage</option>
              <option value="TASK">Task</option>
            </Form.Select>
          </div>
          
          <div className="col-md-3">
            <Form.Select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="under_review">Under Review</option>
              <option value="resolved">Resolved</option>
            </Form.Select>
          </div>

          <div className="col-md-3">
            <InputGroup>
              <InputGroup.Text>
                <FiSearch />
              </InputGroup.Text>
              <Form.Control
                type="search"
                name="search"
                placeholder="Search reports..."
                value={filters.search}
                onChange={handleFilterChange}
              />
            </InputGroup>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-3">Loading reports...</p>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <>
          <Table striped hover responsive>
            <thead>
              <tr>
                {user.role === 'supervisor' && <th>Reported By</th>}
                <th>Type</th>
                <th>Status</th>
                <th>Project</th>
                <th>Date</th>
                <th>Files</th>
                {user.role === 'supervisor' && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {reports.map(report => (
                <tr key={report.id}>
                  {user.role === 'supervisor' && (
                    <td>{report.reportedBy?.name || 'Unknown'}</td>
                  )}
                  <td>
                    <Badge bg="info">{report.type}</Badge>
                  </td>
                  <td>
                    <Badge 
                      bg={
                        report.status === 'resolved' ? 'success' :
                        report.status === 'under_review' ? 'warning' : 'secondary'
                      }
                    >
                      {report.status}
                    </Badge>
                  </td>
                  <td>{report.project?.name}</td>
                  <td>{new Date(report.createdAt).toLocaleDateString()}</td>
                  <td>{report.filesCount} files</td>
                  {user.role === 'supervisor' && (
                    <td>

                       <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => navigate(`/reports/${report.id}`)}
                      >
                        <FiEye />
                      </Button>
                      
                      {report.status !== 'resolved' && (
                        <Button
                          variant="outline-success"
                          size="sm"
                          disabled={resolvingId === report.id}
                          onClick={() => handleResolve(report.id)}
                        >
                          {resolvingId === report.id ? (
                            <Spinner size="sm" animation="border" />
                          ) : (
                            <FiCheck />
                          )}
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Pagination */}
          <div className="d-flex justify-content-center mt-4">
            <Pagination>
              <Pagination.Prev 
                disabled={filters.page === 1}
                onClick={() => handlePageChange(filters.page - 1)}
              />
              {Array.from({ length: meta.totalPages }, (_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === filters.page}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next 
                disabled={filters.page === meta.totalPages}
                onClick={() => handlePageChange(filters.page + 1)}
              />
            </Pagination>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportList;